import { useEffect, useId, useRef, useState } from "react";

import { useIsTouch } from "../lib/media.js";
import { formatTime, parseTime, quarterHours, shiftTime } from "../lib/parse-time.js";

/**
 * A time you type rather than pick.
 *
 * This replaced a dropdown of ninety-six quarter hours, which was quick for the
 * times it had and useless for the ones it did not — a haircut at 2:40 could
 * not be entered at all. So: a text field that takes "240p" or "2:40 pm" or
 * "1440" and understands all three.
 *
 * Three things stop that being worse than the dropdown it replaced.
 *
 * The quarter hours survive as a datalist, so picking still beats typing when
 * the time is on a quarter and the list never gets in the way when it is not.
 *
 * What was understood is shown while typing, before the field is left. Nobody
 * should have to save an event to discover whether "240p" meant what they
 * hoped, and it is how the shorthand gets discovered in the first place.
 *
 * And what cannot be read is refused rather than guessed at. A time field that
 * turns something it did not understand into a plausible wrong time is worse
 * than one that says no.
 *
 * On a touch device none of that applies, because the platform already has a
 * better answer: iOS's three-reel wheel and Android's clock dial are the
 * controls people already know, they are built for thumbs, and they are a
 * system surface no web page can match. So a coarse pointer gets a plain
 * `input type="time"` and Frankie stays out of the way. It stores "HH:mm",
 * which is exactly what that input speaks, so nothing has to be translated.
 */

const SUGGESTIONS = quarterHours();

export function TimeField({
  value,
  onChange,
  label,
  allowEmpty = false,
  emptyLabel = "no end",
}: {
  /** "HH:mm", or "" when empty and allowed. */
  value: string;
  onChange: (next: string) => void;
  label: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
}): React.JSX.Element {
  const listId = useId();
  const touch = useIsTouch();
  const [text, setText] = useState(() => (value === "" ? "" : formatTime(value)));
  const [editing, setEditing] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  /*
   * While the field is being typed in it owns what it shows; outside that the
   * saved value does. Without the guard, a keystroke would be overwritten by
   * the value it is in the middle of changing.
   */
  useEffect(() => {
    if (!editing) setText(value === "" ? "" : formatTime(value));
  }, [value, editing]);

  const typed = text.trim();
  const understood = typed === "" ? null : parseTime(typed);

  /*
   * Only worth showing while it is telling you something you cannot already
   * see — "2:40 pm" under a field reading "2:40 pm" is furniture.
   */
  const preview =
    editing && understood !== null && formatTime(understood) !== typed.toLowerCase()
      ? formatTime(understood)
      : null;

  const unreadable = editing && typed !== "" && understood === null;

  function commit(): void {
    setEditing(false);
    if (typed === "") {
      if (allowEmpty) onChange("");
      else setText(value === "" ? "" : formatTime(value));
      return;
    }
    if (understood === null) {
      // Put back what was there. Refusing beats inventing a time.
      setText(value === "" ? "" : formatTime(value));
      return;
    }
    onChange(understood);
    setText(formatTime(understood));
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
      input.current?.blur();
      return;
    }

    if (event.key === "Escape") {
      setEditing(false);
      setText(value === "" ? "" : formatTime(value));
      input.current?.blur();
      return;
    }

    // Nudging beats retyping when a time is nearly right. An hour with shift.
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      const base = understood ?? (value === "" ? "09:00" : value);
      const step = (event.shiftKey ? 60 : 5) * (event.key === "ArrowUp" ? 1 : -1);
      const next = shiftTime(base, step);
      event.preventDefault();
      onChange(next);
      setText(formatTime(next));
    }
  }

  if (touch) {
    return (
      <label className="row">
        <span>{label}</span>
        <span className="timefield">
          <input
            type="time"
            className="timefield__input timefield__input--native"
            /*
             * A minute at a time. The default is already 60 seconds, but the
             * whole point of replacing the dropdown was that quarter hours are
             * not enough, so it is worth saying rather than inheriting.
             */
            step={60}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </span>
      </label>
    );
  }

  return (
    <label className="row">
      <span>{label}</span>
      <span className="timefield">
        <input
          ref={input}
          type="text"
          className={`timefield__input${unreadable ? " is-unreadable" : ""}`}
          inputMode="text"
          autoComplete="off"
          list={listId}
          placeholder={allowEmpty ? emptyLabel : "9:00 am"}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={(e) => {
            setEditing(true);
            e.target.select();
          }}
          onBlur={commit}
          onKeyDown={onKeyDown}
        />
        <datalist id={listId}>
          {SUGGESTIONS.map((option) => (
            <option key={option.value} value={option.label} />
          ))}
        </datalist>
        {preview !== null && <span className="timefield__hint">{preview}</span>}
        {unreadable && <span className="timefield__hint is-unreadable">?</span>}
      </span>
    </label>
  );
}
